import { useTranslations } from "@zapier/i18n";
import { useInterfaceConfig } from "@zapier/client";
import { Checkbox, Field, TextInput } from "@zapier/design-system";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

export interface EmbedDomainRestrictionFieldProps {
  interfaceId: string;
}

/**
 * Validates that the input is a valid domain with optional wildcard and no protocol or path.
 * From https://gitlab.com/zapier/team-fpv/interfaces/-/blob/c837d1180460e28cc0cd9f6120d36dd898cf7284/packages/service/src/utils/regex.ts#L78-79
 */
const WILDCARD_DOMAIN_REGEX =
  /^(\*\.){0,1}([a-zA-Z0-9-]+\.){1,}[a-zA-Z0-9-]+(:[0-9]{1,5}){0,1}$/;

const DOMAIN_ERROR_MSG =
  'Domains must be separated by commas and must not include the protocol (e.g. "http://" or "https://") or path (e.g. "/example/path").';

export function EmbedDomainRestrictionField({
  interfaceId,
}: EmbedDomainRestrictionFieldProps) {
  const t = useTranslations("embed-domain-restriction");

  const { control, trigger, watch, setValue } = useFormContext();

  const { config, isLoading } = useInterfaceConfig({ id: interfaceId });

  // Set default values based on interface config
  useEffect(() => {
    if (config) {
      setValue("allowedDomains", config.embedHosts.join(", "));
      setValue("restrictDomains", config.embedHosts.length > 0);
    }
  }, [config, setValue]);

  return (
    <>
      <Controller
        control={control}
        disabled={isLoading}
        name="restrictDomains"
        render={({ field }) => (
          <Checkbox
            isSelected={field.value}
            onChange={() => {
              field.onChange(!field.value);
              trigger("allowedDomains");
            }}
          >
            {t("checkbox.restrict-domains-label")}
          </Checkbox>
        )}
      />
      <Controller
        control={control}
        name="allowedDomains"
        rules={{
          validate(value: string) {
            const isDomainRestricted = watch("restrictDomains");
            if (!isDomainRestricted || !value) {
              return true;
            }
            const areAllValid = value
              .split(",")
              .map((d) => d.trim())
              .every((domain) => WILDCARD_DOMAIN_REGEX.test(domain));
            return areAllValid || DOMAIN_ERROR_MSG;
          },
        }}
        render={({ field, fieldState }) => (
          <Field
            isDisabled={watch("restrictDomains") !== true || isLoading}
            label={t("field.allowed-domains-label")}
            renderInput={(inputProps) => (
              <TextInput
                {...inputProps}
                {...field}
                isErrored={Boolean(fieldState.error)}
                placeholder={
                  isLoading
                    ? t("field.allowed-domains-placeholder-loading")
                    : t("field.allowed-domains-placeholder-default")
                }
                type="text"
              />
            )}
            helpTextLineClamp={2}
            error={fieldState.error?.message}
            renderHelpText={() =>
              fieldState.error?.message ? undefined : (
                <>{t("field.allowed-domains-help-text")}</>
              )
            }
          />
        )}
      />
    </>
  );
}
